import type { NetworkPlayer } from '../network/protocol';

export interface RunnerManifestEntry {
  id: string;
  displayName: string;
  local: boolean;
  bot: boolean;
  connected: boolean;
  extracted: boolean;
  trainNumber: number;
  routePercent: number;
  carriedValue: number;
  eliminations: number;
  falls: number;
  health: number;
  status: string;
}

export function buildRunnerManifest(
  players: readonly NetworkPlayer[],
  localPlayerID: string,
  routeStartZ: number,
  routeFinishZ: number,
): RunnerManifestEntry[] {
  const routeLength = Math.max(1, routeStartZ-routeFinishZ);
  return players.map((player) => {
    const health = Math.max(0, player.health ?? 100);
    const extracted = player.extracted;
    return {
      id: player.id,
      displayName: player.displayName,
      local: player.id === localPlayerID,
      bot: Boolean(player.bot),
      connected: player.connected,
      extracted,
      trainNumber: Math.max(1, player.trainNumber ?? 1),
      routePercent: extracted ? 100 : Math.round(clamp((routeStartZ-player.position.z)/routeLength, 0, 1)*100),
      carriedValue: Math.max(0, player.carriedValue),
      eliminations: Math.max(0, player.eliminations ?? 0),
      falls: Math.max(0, player.falls),
      health,
      status: runnerStatus(player, health),
    };
  }).sort(compareManifestEntries);
}

function compareManifestEntries(left: RunnerManifestEntry, right: RunnerManifestEntry): number {
  const state = manifestState(left)-manifestState(right);
  if (state !== 0) return state;
  if (!left.extracted && !right.extracted) {
    if (left.trainNumber !== right.trainNumber) return right.trainNumber-left.trainNumber;
    if (left.routePercent !== right.routePercent) return right.routePercent-left.routePercent;
  }
  if (left.eliminations !== right.eliminations) return right.eliminations-left.eliminations;
  if (left.carriedValue !== right.carriedValue) return right.carriedValue-left.carriedValue;
  return left.displayName.localeCompare(right.displayName);
}

function manifestState(entry: RunnerManifestEntry): number {
  if (!entry.connected) return 2;
  return entry.extracted ? 1 : 0;
}

function runnerStatus(player: NetworkPlayer, health: number): string {
  if (!player.connected) return 'SIGNAL LOST';
  if (player.extracted) return 'BANKED';
  if (player.grabbedById) return 'GRABBED';
  if (health <= 35) return 'CRITICAL';
  if (player.climbing) return 'CLIMBING';
  if (player.weapon?.id) return 'ARMED';
  if (player.bot) return 'YARD BOT';
  return 'RUNNING';
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export class RunRoster {
  private readonly panel: HTMLElement;
  private readonly list: HTMLOListElement;
  private readonly count: HTMLElement;
  private readonly leader: HTMLElement;
  private active = false;
  private held = false;
  private localPlayerID = '';
  private renderKey = '';
  private latest: { players: readonly NetworkPlayer[]; routeStartZ: number; routeFinishZ: number } | null = null;

  public constructor(root: ParentNode) {
    this.panel = requireElement<HTMLElement>(root, '[data-run-roster]');
    this.list = requireElement<HTMLOListElement>(root, '[data-run-roster-list]');
    this.count = requireElement<HTMLElement>(root, '[data-run-roster-count]');
    this.leader = requireElement<HTMLElement>(root, '[data-run-roster-leader]');
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.hide);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  public setActive(active: boolean, localPlayerID = ''): void {
    this.active = active;
    this.localPlayerID = active ? localPlayerID : '';
    this.panel.hidden = !active;
    if (!active) {
      this.latest = null;
      this.renderKey = '';
      this.list.replaceChildren();
      this.hide();
    }
  }

  public update(players: readonly NetworkPlayer[], routeStartZ: number, routeFinishZ: number): void {
    if (!this.active) return;
    // Snapshots continue while Tab is closed. Keep only the latest source; do
    // not sort, format or rebuild an invisible DOM tree on every network tick.
    this.latest = { players, routeStartZ, routeFinishZ };
    if (this.held) this.renderLatest();
  }

  private renderLatest(): void {
    if (!this.latest) return;
    const { players, routeStartZ, routeFinishZ } = this.latest;
    const entries = buildRunnerManifest(players, this.localPlayerID, routeStartZ, routeFinishZ);
    const key = entries.map((entry) => [
      entry.id,
      entry.displayName,
      entry.local ? 1 : 0,
      entry.bot ? 1 : 0,
      entry.connected ? 1 : 0,
      entry.extracted ? 1 : 0,
      entry.trainNumber,
      entry.routePercent,
      entry.carriedValue,
      entry.eliminations,
      entry.falls,
      entry.health,
      entry.status,
    ].join(':')).join('|');
    if (key === this.renderKey) return;
    this.renderKey = key;
    const live = entries.filter((entry) => entry.connected && !entry.extracted).length;
    this.count.textContent = `${live} LIVE // ${entries.length} MANIFESTED`;
    const frontRunner = entries.find((entry) => entry.connected && !entry.extracted);
    this.leader.textContent = frontRunner
      ? `FRONT RUNNER // ${frontRunner.local ? 'YOU' : frontRunner.displayName.toUpperCase()} // TRAIN ${frontRunner.trainNumber}`
      : 'THE LINE IS CLEAR';
    this.list.innerHTML = entries.map((entry, index) => renderEntry(entry, index+1)).join('');
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.active || event.code !== 'Tab') return;
    event.preventDefault();
    if (this.held) return;
    this.held = true;
    this.renderLatest();
    this.panel.classList.add('is-visible');
    this.panel.setAttribute('aria-hidden', 'false');
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (!this.active || event.code !== 'Tab') return;
    event.preventDefault();
    this.hide();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.hide();
  };

  private readonly hide = (): void => {
    this.held = false;
    this.panel.classList.remove('is-visible');
    this.panel.setAttribute('aria-hidden', 'true');
  };
}

function renderEntry(entry: RunnerManifestEntry, rank: number): string {
  const classes = [
    entry.local ? 'is-local' : '',
    entry.bot ? 'is-bot' : '',
    entry.extracted ? 'is-extracted' : '',
    !entry.connected ? 'is-disconnected' : '',
  ].filter(Boolean).join(' ');
  const badge = entry.local ? 'YOU' : entry.bot ? 'BOT' : 'RUNNER';
  return `<li class="${classes}" data-runner-id="${escapeMarkup(entry.id)}">
    <span class="run-roster__rank">${String(rank).padStart(2, '0')}</span>
    <span class="run-roster__identity"><b>${escapeMarkup(entry.displayName)}</b><small><i>${badge}</i>${entry.status}</small></span>
    <span><small>TRAIN</small><b>${entry.trainNumber}</b></span>
    <span><small>ROUTE</small><b>${entry.routePercent}%</b></span>
    <span class="run-roster__haul"><small>HAUL</small><b>₵${entry.carriedValue}</b></span>
    <span><small>KO</small><b>${entry.eliminations}</b></span>
    <span><small>FALLS</small><b>${entry.falls}</b></span>
    <span class="run-roster__suit"><small>SUIT</small><b>${entry.health}</b></span>
  </li>`;
}

function escapeMarkup(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    "'": '&#39;',
    '"': '&quot;',
  })[character] ?? character);
}

function requireElement<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing runner manifest element: ${selector}`);
  return element;
}
